import './UnitPhoto.sass';

import React, { FC, useState } from 'react';
import { Link } from 'react-router5';

import { UnitPhotoProps } from './UnitPhoto.interface';
import { useUnitName } from '$hooks';
import { Online } from '$shared/components';
import { classNames as cn } from '$utils';
import { PROFILE, RESOLVE } from '$shared/constants/pages';

const UnitPhotoComponent: FC<UnitPhotoProps> = ({
  unit,
  onlineIndicator = true,
  routeName,
  hasStory = false,
  hitSlop = 0,
  hasLive = false,
  size = 'medium',
  nftIndicator = true,
  round = false,
  onClick,
  className,
}) => {
  const name = useUnitName(unit);
  const [isFailed, setFailed] = useState(false);

  if (!unit) {
    return (
      <div
        className={cn('UnitPhoto', size, className, {
          round,
        })}
      />
    );
  }

  const isUser = !('name' in unit);
  const failedImageUrl = '/assets/ava_placeholder.png';

  const { photo, thumbnail }: { photo: string; thumbnail?: string } = isUser
    ? {
        photo: isFailed ? failedImageUrl : unit.photo,
        thumbnail: unit.thumbnail,
      }
    : { photo: isFailed ? failedImageUrl : unit.photoLarge };

  const Wrapper =
    routeName !== null && !onClick
      ? ({ children, ...props }) => (
          <Link
            routeName={routeName || unit.domain ? RESOLVE : PROFILE}
            routeParams={unit.domain ? { domain: unit.domain } : { id: unit!.id }}
            {...props}
          >
            {children}
          </Link>
        )
      : ({ children, ...props }) => (
          <div onClick={onClick} {...props}>
            {children}
          </div>
        );

  const hasNft = isUser && nftIndicator && unit.hasNftPhoto;
  const hasNftLabel = hasNft && ['large', 'extra-large'].includes(size);
  const hasNftBorder = hasNft && !hasNftLabel;

  return (
    <Wrapper
      className={cn('UnitPhoto', size, className, {
        nft: hasNftBorder,
        hasStory,
        hasLive,
        round,
        active: onClick,
      })}
      style={{
        backgroundImage: thumbnail ? `url(${thumbnail})` : undefined,
        '--hitSlop': hitSlop + 'px',
      }}
    >
      <img onError={(e: any) => setFailed(true)} src={photo} alt={name} />
      {isUser && onlineIndicator && <Online inPhoto userId={unit.id} />}
      {hasNftLabel && <div className="UnitPhoto__nftLabel">Nft</div>}
      {hasLive && <div className="UnitPhoto__liveLabel">Live</div>}
      {!!hitSlop && <div className="UnitPhoto__hitSlop" />}
    </Wrapper>
  );
};

export const UnitPhoto = React.memo(UnitPhotoComponent);
